/**
 * `tokensToCssVars(tokens)` — projects BrandingTokens V2 onto a flat
 * map of CSS custom properties.
 *
 * Naming scheme: `--scrolly-brand-{category}-{key}` with nested paths
 * flattened via additional hyphens (e.g. `typography.display.family` →
 * `--scrolly-brand-typography-display-family`). Lower-case.
 *
 * Only fields that have meaningful CSS-side effects are emitted:
 *   - colors.* (8 tokens)
 *   - typography.{display,ui}.family
 *   - shape.cornerRadius (px)
 *   - shape.shadowDepth (0–3 ordinal, mapped to box-shadow string)
 *   - motion.duration (ms)
 *   - motion.easing (string)
 *   - logo.light.url / logo.dark.url / logo.favicon.url (for background-image use)
 *
 * Fields not emitted (by design):
 *   - version, identity, logo.*.position (layout props, not style tokens)
 *   - showLogoInViewer (a boolean feature flag, not a CSS value — the
 *     Viewer reads it directly from the tokens object, not via CSS)
 *   - social, watermark, credit (Phase 10.5b, not yet wired into CSS)
 *
 * Return type is `Record<string, string>` so consumers can spread the
 * result into `style={{...}}` on a DOM element or render it into an
 * inline `<style>` block in `+Head.tsx`.
 */

import type { BrandingTokens } from './tokens.types.js';

const SHADOW_CSS: Record<0 | 1 | 2 | 3, string> = {
  0: 'none',
  1: '0 1px 3px rgba(0,0,0,0.06)',
  2: '0 4px 12px rgba(0,0,0,0.1)',
  3: '0 12px 32px rgba(0,0,0,0.16)',
};

export function tokensToCssVars(tokens: BrandingTokens): Record<string, string> {
  const out: Record<string, string> = {};

  // ── Colors (8 tokens) ──
  out['--scrolly-brand-colors-primary'] = tokens.colors.primary;
  out['--scrolly-brand-colors-secondary'] = tokens.colors.secondary;
  out['--scrolly-brand-colors-accent'] = tokens.colors.accent;
  // W1 — Cover-Akzent nur emittieren, wenn die Org einen gesetzt hat. Ohne
  // Emission greift im Viewer der Fallback auf das Scrolly-Pink (CoverSection).
  if (tokens.cover?.accent) {
    out['--scrolly-brand-cover-accent'] = tokens.cover.accent;
  }
  out['--scrolly-brand-colors-warn'] = tokens.colors.warn;
  out['--scrolly-brand-colors-bg'] = tokens.colors.bg;
  out['--scrolly-brand-colors-surface'] = tokens.colors.surface;
  out['--scrolly-brand-colors-border'] = tokens.colors.border;
  out['--scrolly-brand-colors-muted'] = tokens.colors.muted;

  // ── Typography (display + ui font families) ──
  out['--scrolly-brand-typography-display-family'] =
    quoteFontFamily(tokens.typography.display.family);
  out['--scrolly-brand-typography-ui-family'] =
    quoteFontFamily(tokens.typography.ui.family);

  // ── Shape ──
  out['--scrolly-brand-shape-corner-radius'] = `${tokens.shape.cornerRadius}px`;
  out['--scrolly-brand-shape-shadow'] = SHADOW_CSS[tokens.shape.shadowDepth];

  // ── Motion ──
  out['--scrolly-brand-motion-duration'] = `${tokens.motion.duration}ms`;
  out['--scrolly-brand-motion-easing'] = easingToCss(tokens.motion.easing);

  // ── Logo URLs (rendered as `url(...)` so they drop straight into
  //    background-image properties without extra string-mangling) ──
  if (tokens.logo.light.url) {
    out['--scrolly-brand-logo-light-url'] = `url("${tokens.logo.light.url}")`;
  }
  if (tokens.logo.dark.url) {
    out['--scrolly-brand-logo-dark-url'] = `url("${tokens.logo.dark.url}")`;
  }
  if (tokens.logo.favicon.url) {
    out['--scrolly-brand-logo-favicon-url'] =
      `url("${tokens.logo.favicon.url}")`;
  }

  // ── Share-Viewer-Hintergrund (jan-17) ──
  // `background` ist ein fertiger Composite-CSS-String (solid rgba ODER
  // linear-gradient). Nur emittieren, wenn gesetzt — sonst greift in der
  // Viewer-`var(--…, fallback)`-Referenz der Avatar-Radial-Fallback.
  // `?.` defensiv: bei Rolling-Deploy kann ein älteres Backend Tokens
  // ohne `share`-Feld liefern.
  const share = tokens.share;
  if (share?.background) {
    out['--scrolly-brand-share-background'] = share.background;
    // Heller Unterbau NUR wenn ein Share-BG gesetzt ist: der Viewer legt die
    // Opacity dann über HELL (wie der Editor-Preview) statt über das dunkle
    // #111. Ohne Share-BG NICHT emittiert → Avatar-Fallback über #111 bleibt.
    out['--scrolly-brand-share-base'] = '#ffffff';
  }
  out['--scrolly-brand-share-blur'] = `${share?.blur ?? 0}px`;
  out['--scrolly-brand-share-opacity'] = `${(share?.opacity ?? 100) / 100}`;

  return out;
}

/**
 * Wraps the family in double-quotes so multi-word families (e.g.
 * "Instrument Serif") are valid CSS font-family values. Appends a
 * sensible fallback stack keyed on whether the family looks serif-ish
 * (display slot) — keeps the viewer readable if the web-font fails
 * to load. The heuristic is cheap and conservative: any family string
 * that contains "Serif" / "Display" / "Playfair" picks the serif stack;
 * everything else picks sans-serif. Consumers that want a different
 * fallback should not use this helper and emit the family raw.
 */
function quoteFontFamily(family: string): string {
  const fallback = /serif|display|playfair/i.test(family)
    ? 'Georgia, serif'
    : '-apple-system, system-ui, sans-serif';
  return `"${family}", ${fallback}`;
}

/**
 * Maps the token-level easing keyword onto the corresponding CSS
 * timing-function. `spring` doesn't have a native CSS equivalent, so
 * we emit a cubic-bezier approximation that feels similar enough for
 * the durations we use (100-800ms).
 */
function easingToCss(easing: BrandingTokens['motion']['easing']): string {
  switch (easing) {
    case 'linear':
      return 'linear';
    case 'in-cubic':
      return 'cubic-bezier(0.32, 0, 0.67, 0)';
    case 'out-cubic':
      return 'cubic-bezier(0.33, 1, 0.68, 1)';
    case 'in-out-cubic':
      return 'cubic-bezier(0.65, 0, 0.35, 1)';
    case 'spring':
      return 'cubic-bezier(0.5, 1.5, 0.5, 1)';
  }
}
